import api from './api';

export interface CaptchaSettings {
  enabled: boolean;
  site_key?: string;
  site_key_configured?: boolean;
  secret_key?: string;
  secret_key_configured?: boolean;
  secret_key_masked?: string | null;
  provider: string;
}

export interface RedisSettings {
  enabled: boolean;
  host: string;
  port: number;
  db: number;
  password?: string;
  password_configured?: boolean;
  password_masked?: string | null;
}

export interface RateLimitSettings {
  enabled: boolean;
  ip_attempts: number;
  email_attempts_hour: number;
  email_attempts_5min: number;
  captcha_trigger: number;
}

export interface StripeModeKeys {
  public_key?: string;
  secret_key?: string;
  secret_key_configured?: boolean;
  secret_key_masked?: string | null;
  webhook_secret?: string;
  webhook_secret_configured?: boolean;
  webhook_secret_masked?: string | null;
  plus_price_id?: string | null;
  pro_price_id?: string | null;
  intro_fee_price_id?: string | null;
}

export interface StripeSettings {
  enabled: boolean;
  public_key?: string;
  secret_key?: string;
  secret_key_configured?: boolean;
  secret_key_masked?: string | null;
  webhook_secret?: string;
  webhook_secret_configured?: boolean;
  webhook_secret_masked?: string | null;
  mode: 'test' | 'live';
  /** Sandbox keys stored separately from live (never overwrite each other). */
  test?: StripeModeKeys;
  /** Live keys stored separately from test. */
  live?: StripeModeKeys;
  plus_price_id?: string;
  pro_price_id?: string;
  intro_fee_price_id?: string;
}

export interface WiseSettings {
  enabled: boolean;
  api_key?: string;
  api_key_configured?: boolean;
  api_key_masked?: string | null;
  client_id?: string;
  client_secret?: string;
  client_secret_configured?: boolean;
  client_secret_masked?: string | null;
  webhook_secret?: string;
  webhook_secret_configured?: boolean;
  webhook_secret_masked?: string | null;
  profile_id?: string;
}

export interface PayoutSettings {
  max_wise_email_methods: number;
  max_local_bank_methods: number;
  max_total_methods: number;
}

export interface GoAffProSettings {
  enabled: boolean
  public_token?: string
  public_token_configured?: boolean
  public_token_masked?: string | null
  private_token?: string
  private_token_configured?: boolean
  private_token_masked?: string | null
}

export interface PaymentSettings {
  stripe: StripeSettings;
  goaffpro?: GoAffProSettings;
  wise: WiseSettings;
  payout: PayoutSettings;
}

export interface InvoiceSettings {
  company_name: string;
  company_address: string;
  contact_email: string;
}

export interface SecuritySettings {
  captcha: CaptchaSettings;
  redis: RedisSettings;
  rate_limiting: RateLimitSettings;
  two_factor_auth: boolean;
  password_complexity: string;
  session_timeout: number;
}

export interface SystemSettings {
  platform_fee: number;
  enable_registration: boolean;
  email_notifications: boolean;
  maintenance_mode: boolean;
  human_tutor_layer_enabled?: boolean;
}

export interface ProductModeSettings {
  human_tutor_layer_enabled: boolean;
}

export type OpenRouterRoutingMode = 'free_router' | 'fixed_model';

export interface OpenRouterSettings {
  enabled: boolean;
  api_key?: string | null;
  api_key_configured?: boolean;
  api_key_masked?: string | null;
  routing_mode?: OpenRouterRoutingMode;
  fixed_model_id?: string;
  vision_model_id?: string;
  default_model: string;
  default_pin_v1_applied?: boolean;
  site_url?: string | null;
  app_title?: string | null;
}

export interface GroqSettings {
  enabled: boolean;
  api_key?: string | null;
  api_key_configured?: boolean;
  api_key_masked?: string | null;
  model?: string;
}

export interface TtsSettings {
  enabled: boolean;
  service_url?: string | null;
  max_chars?: number;
}

export interface ReviewBuildSettings {
  routing_mode?: OpenRouterRoutingMode;
  fixed_model_id?: string;
  active_template_id?: string;
  include_course_materials?: boolean;
  enable_chunked_build?: boolean;
  /** @deprecated Plan-tiered caps replace this global limit. */
  daily_build_limit?: number;
  free_daily_build_limit?: number;
  plus_daily_build_limit?: number;
  pro_daily_build_limit?: number;
}

export interface ReviewTemplateEntry {
  id: string;
  name: string;
  content: string;
  version: number;
  based_on?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface AISettings {
  openrouter: OpenRouterSettings;
  groq?: GroqSettings;
  tts?: TtsSettings;
  openrouter_functions_model?: string;
  review_build?: ReviewBuildSettings;
  review_templates?: Record<string, ReviewTemplateEntry>;
  review_template_overrides?: Record<string, string>;
}

export interface AISettingsUpdate {
  openrouter?: OpenRouterSettings;
  groq?: GroqSettings;
  tts?: TtsSettings;
  openrouter_functions_model?: string;
  review_build?: ReviewBuildSettings;
  review_templates?: Record<string, ReviewTemplateEntry>;
  review_template_overrides?: Record<string, string>;
}

export interface IntegrationsSettings {
  google_oauth: {
    enabled: boolean;
    client_id?: string | null;
    client_secret?: string | null;
    client_secret_configured?: boolean;
    client_secret_masked?: string | null;
    webhook_url?: string | null;
  };
  brevo: {
    enabled: boolean;
    api_key?: string | null;
    api_key_configured?: boolean;
    api_key_masked?: string | null;
    sender_email?: string | null;
    sender_name: string;
    webhook_url?: string | null;
  };
}

export interface ReferralProgramSettings {
  enabled: boolean;
  global_commission_percent: number;
  hold_window_days: number;
  min_payout_cents: number;
  invite_email_daily_limit: number;
}

export interface PlatformSettings {
  system: SystemSettings;
  security: SecuritySettings;
  payment: PaymentSettings;
  invoice: InvoiceSettings;
  ai: AISettings;
  integrations: IntegrationsSettings;
  referral?: ReferralProgramSettings;
}

export interface PlatformSettingsUpdate {
  system?: SystemSettings;
  security?: SecuritySettings;
  payment?: PaymentSettings;
  invoice?: InvoiceSettings;
  ai?: AISettingsUpdate;
  integrations?: IntegrationsSettings;
  referral?: ReferralProgramSettings;
}

export interface TestResult {
  success: boolean;
  message: string;
  details: string;
}

class PlatformSettingsService {
  private baseUrl = '/platform-settings';

  async getSettings(): Promise<PlatformSettings> {
    try {
      // Trailing slash matches FastAPI route and avoids 307 -> http redirect (drops Authorization).
      const response = await api.get(`${this.baseUrl}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching platform settings:', error);
      throw error;
    }
  }

  async getPayoutSettings(): Promise<PayoutSettings> {
    try {
      const settings = await this.getSettings();
      return settings.payment.payout;
    } catch (error) {
      console.error('Error fetching payout settings:', error);
      throw error;
    }
  }

  async updateSettings(settings: PlatformSettingsUpdate): Promise<PlatformSettings> {
    try {
      const response = await api.put(`${this.baseUrl}/`, settings);
      return response.data;
    } catch (error) {
      console.error('Error updating platform settings:', error);
      throw error;
    }
  }

  async getProductMode(): Promise<ProductModeSettings> {
    try {
      const response = await api.get(`${this.baseUrl}/product-mode`);
      return response.data;
    } catch (error) {
      console.error('Error fetching product mode:', error);
      throw error;
    }
  }

  async testCaptchaConfiguration(): Promise<TestResult> {
    try {
      const response = await api.post(`${this.baseUrl}/test-captcha`);
      return response.data;
    } catch (error) {
      console.error('Error testing CAPTCHA configuration:', error);
      throw error;
    }
  }

  async testRedisConnection(): Promise<TestResult> {
    try {
      const response = await api.post(`${this.baseUrl}/test-redis`);
      return response.data;
    } catch (error) {
      console.error('Error testing Redis connection:', error);
      throw error;
    }
  }

  async testStripeConfiguration(): Promise<TestResult> {
    try {
      const response = await api.post(`${this.baseUrl}/test-stripe`);
      return response.data;
    } catch (error) {
      console.error('Error testing Stripe configuration:', error);
      throw error;
    }
  }

  async testWiseConfiguration(): Promise<TestResult> {
    try {
      const response = await api.post(`${this.baseUrl}/test-wise`);
      return response.data;
    } catch (error) {
      console.error('Error testing Wise configuration:', error);
      throw error;
    }
  }

  async testGoAffProConfiguration(): Promise<TestResult> {
    try {
      const response = await api.post(`${this.baseUrl}/test-goaffpro`);
      return response.data;
    } catch (error) {
      console.error('Error testing GoAffPro configuration:', error);
      throw error;
    }
  }

  async testOpenRouterConfiguration(): Promise<TestResult> {
    try {
      const response = await api.post(`${this.baseUrl}/test-openrouter`);
      return response.data;
    } catch (error) {
      console.error('Error testing OpenRouter configuration:', error);
      throw error;
    }
  }

  async testGroqConfiguration(): Promise<TestResult> {
    try {
      const response = await api.post(`${this.baseUrl}/test-groq`);
      return response.data;
    } catch (error) {
      console.error('Error testing Groq configuration:', error);
      throw error;
    }
  }
}

export default new PlatformSettingsService();
